import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import { S3Image } from './S3Image';
import { isRefId, resolveRefIdToUuid } from '../utils/bookIdResolver';
import { VidyaFigure } from './VidyaFigure';
import { MermaidDiagram } from './MermaidDiagram';
import { VidyaSvg } from './VidyaSvg';
import { VideoMoments } from './VideoMoments';

/**
 * Math rendering — production-standard chain:
 *
 *   remark-math (AST-level tokenizer)
 *     ↓ parses `$$..$$` as `math` nodes (display)
 *     ↓ singleDollarTextMath: false — `$N` always literal text
 *   rehype-katex (HAST converter)
 *     ↓ replaces math nodes with KaTeX HAST nodes (real <svg>/<span>)
 *     ↓ errorColor — KaTeX errors stay contained in `.katex-error` span
 *   react-markdown renders the HAST as actual React elements
 *
 * The previous hand-rolled `preprocessMath` regex chain was deleted —
 * it round-tripped math HTML through `rehype-raw`'s string parser,
 * which (a) leaked SVG path coordinates as visible text when the parser
 * mishandled the radical's <svg>, and (b) re-injected the KaTeX error
 * fallback `<code>$$..$$</code>` back into the markdown tokenizer,
 * causing currency `$N` + math `$x$` collisions to render as gibberish.
 *
 * This is the chain HuggingFace chat-ui, assistant-ui, and Streamdown
 * ship. The "list-item math limitation" comment that justified the old
 * approach was from 2020 and no longer applies in remark-math@6.
 *
 * Inline math: narrator emits `\(..\)` and `\[..\]` — the same
 * convention ChatGPT/Canvas use internally per the 2025 OpenAI
 * cookbook. We then translate to `$..$` / `$$..$$` BEFORE remark-math
 * runs (see `translateBracketedMath` below), so remark-math sees only
 * dollar-delimited math.
 *
 * Why bracketed delimiters not single-dollar:
 *   • Eliminates currency collision (`$5` is just $5, never math)
 *   • Eliminates streaming partial-`$` ambiguity (a half-streamed `\(`
 *     is unambiguous; a half-streamed `$` could go either way)
 *   • Unambiguous to the LLM — production systems converged on this
 *
 * Backward compat: legacy content with `$..$` / `$$..$$` (stored
 * TLDRs, flashcards) continues to render because we keep
 * `singleDollarTextMath: true`. The translator below is purely
 * ADDITIVE — adds `\(..\)` support without removing `$..$`.
 */
const REMARK_MATH_OPTIONS = {
  // True since the translator below converts `\(..\)` → `$..$`. We
  // own every `$` that reaches remark-math, so no currency collision.
  singleDollarTextMath: true as const,
};

const REHYPE_KATEX_OPTIONS = {
  // KaTeX runtime errors stay contained inside a `.katex-error` span
  // and don't leak HTML back into the markdown stream.
  errorColor: '#dc2626',
  // 'ignore' — KaTeX warnings (multi-char sub/sup w/o braces, etc.)
  // log but don't throw. `throwOnError:false` keeps a single bad
  // expression from blanking the whole message.
  strict: 'ignore' as const,
  throwOnError: false,
  // Allow KaTeX `\href`, `\url`, `\includegraphics` etc. — the
  // narrator never emits these but stored notes might. Without this,
  // the whole expression silently fails.
  trust: true,
  // MathML output alongside HTML — accessibility + better text copy.
  output: 'htmlAndMathml' as const,
};

/**
 * Translate ChatGPT-style bracketed math delimiters into the remark-math
 * dollar convention. Minimal logic only — the prompt is responsible for
 * making the LLM emit correct delimiters. Over-engineering normalisation
 * here led to whack-a-mole bugs (see git history if you're curious).
 *
 *   \(...\)        →   $...$
 *   \[ x \]        →   $$x$$           (single-line — stays glued)
 *   \[ multi\nline\] →  $$\n...\n$$    (multi-line — delimiters on own lines)
 *
 * Multi-line display math has its delimiters put on their own lines — and any
 * multi-line `$$..$$` the model emits directly is normalised the same way —
 * because micromark's math extension treats text after an opening `$$` on the
 * same line as a discarded fence info-string, and a closing `$$` glued to a
 * content line is not a valid close. Without this, `$$\begin{array}…\end{array}$$`
 * loses its first line and swallows trailing prose to EOF → KaTeX error.
 * Single-line math is left glued (compact, and avoids list-breakage); we use
 * single `\n`, never blank lines, so indented list content stays in its list.
 *
 * Beyond delimiter placement we don't normalise. If the LLM emits broken output
 * (e.g. wrapping prose in
 * `$$...$$`, concatenating display blocks without separators), we let it
 * render broken so the bug is visible and the prompt can be fixed at the
 * source — not papered over here.
 *
 * NB: in a JS template literal, `$$${X}$$` expands to `$$<X>$$` — three
 * source-`$` chars produce two output dollars because the third `$`
 * combines with `{X}` as interpolation. Four source-`$` would give three
 * output dollars (bug we hit). Two source-`$` gives one (also wrong).
 */
function translateBracketedMath(src: string): string {
  if (!src) return src;
  // Translate ONLY the prose between fenced code blocks. Content inside a
  // ```fence``` must stay byte-exact: converting \( \) → $ $ inside e.g. a
  // vidya-video-moments JSON payload turns a valid `\\(` escape into `\$`,
  // which is not a legal JSON escape — JSON.parse then throws and the block
  // renders as an empty box. Splitting on the fence keeps payloads intact.
  return src
    .split(/(```[\s\S]*?```)/g)
    .map((part, i) =>
      i % 2 === 1
        ? part // a fenced code block — leave its contents untouched
        : part
            // Only MULTI-LINE display math needs its delimiters on their own
            // lines (see the `$$` re-pad below for why). Single-line `\[x\]`
            // stays glued as `$$x$$` — byte-compact and safe. Crucially we use
            // single `\n`, NOT blank lines: a blank line inside a 4-space-
            // indented list item breaks the list, turning following indented
            // prose into an indented code block.
            .replace(/\\\[([\s\S]+?)\\\]/g, (_m, body) => {
              const b = body.trim();
              return b.includes('\n') ? `\n$$\n${b}\n$$\n` : `$$${b}$$`;
            })
            .replace(/\\\(([^\n]+?)\\\)/g, (_m, body) => `$${body.trim()}$`)
            // remark-math (micromark) parses display `$$` like a fenced block:
            // text after the opening `$$` on the SAME line is treated as the
            // fence info-string and DISCARDED, and a closing `$$` glued to the
            // end of a content line is not a valid closing fence — so a
            // multi-line `$$\begin{array}…\end{array}$$` loses its first line
            // and swallows trailing prose to EOF → KaTeX errors → red dump.
            // The model emits these directly (math_addendum tells it to use
            // `$$..$$`), so put any multi-line `$$` block's delimiters on their
            // own lines. Single-line `$$x$$` is left byte-exact (stored
            // content). Single `\n` only — never blank lines (breaks lists).
            .replace(/\$\$([\s\S]+?)\$\$/g, (m, body) =>
              body.includes('\n') ? `\n$$\n${body.trim()}\n$$\n` : m,
            ),
    )
    .join('');
}

interface MarkdownRendererProps {
  content: string;
  className?: string;
  onNoteClick?: (noteId: string) => void;
  onImageClick?: (src: string) => void;
  onPageLinkClick?: (bookId: string, pageNumber: number) => void;
  onTimestampClick?: (sec: number) => void;   // [m:ss](#t=123) seek links (video digest)
}

// Stable anchor id for a heading: prefer its `#t=<sec>` seek link (→ "sec-123") so an
// external nav can scroll to it, else a slug of the heading text.
const headingAnchorId = (node: any): string | undefined => {
  const findTs = (n: any): string | null => {
    if (!n) return null;
    if (n.type === 'link' && typeof n.url === 'string') {
      const m = n.url.match(/^#t=(\d+)/);
      if (m) return `sec-${m[1]}`;
    }
    if (Array.isArray(n.children)) for (const c of n.children) { const r = findTs(c); if (r) return r; }
    return null;
  };
  const ts = findTs(node);
  if (ts) return ts;
  const collect = (n: any): string => !n ? '' : n.type === 'text' ? (n.value || '')
    : Array.isArray(n.children) ? n.children.map(collect).join('') : '';
  const slug = collect(node).toLowerCase().replace(/\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
  return slug || undefined;
};

interface ResolvedPageLinkProps {
  href: string;
  bookId: string;
  pageNum: number;
  children: React.ReactNode;
  onPageLinkClick?: (bookId: string, pageNumber: number) => void;
}

const ResolvedPageLink: React.FC<ResolvedPageLinkProps> = React.memo(({
  href,
  bookId,
  pageNum,
  children,
  onPageLinkClick
}) => {
  const [resolvedBookId, setResolvedBookId] = useState<string>(bookId);
  const [resolvedHref, setResolvedHref] = useState<string>(href);

  useEffect(() => {
    const resolveId = async () => {
      if (!isRefId(bookId)) {
        setResolvedBookId(bookId);
        setResolvedHref(href);
        return;
      }
      const uuid = await resolveRefIdToUuid(bookId);
      if (uuid) {
        setResolvedBookId(uuid);
        setResolvedHref(`/book/${uuid}/page/${pageNum}`);
      } else {
        setResolvedBookId(bookId);
        setResolvedHref(href);
      }
    };
    resolveId();
  }, [bookId, pageNum]);

  return (
    <a
      href={resolvedHref}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onPageLinkClick && resolvedBookId && !isNaN(pageNum)) {
          onPageLinkClick(resolvedBookId, pageNum);
        }
      }}
      className="text-cyan-600 dark:text-cyan-400 hover:underline cursor-pointer font-medium inline-flex items-center gap-1"
      title={`Go to Page ${pageNum}`}
    >
      {children}
    </a>
  );
});

function processBrTags(children: React.ReactNode): React.ReactNode {
  if (typeof children === 'string') {
    const parts = children.split(/<br\s*\/?>/gi);
    if (parts.length === 1) return children;
    return parts.map((part, i) => (
      <React.Fragment key={i}>
        {part}
        {i < parts.length - 1 && <br />}
      </React.Fragment>
    ));
  }
  if (Array.isArray(children)) {
    return children.map((child, i) => (
      <React.Fragment key={i}>{processBrTags(child)}</React.Fragment>
    ));
  }
  return children;
}

function extractTextFromChildren(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (!children) return '';
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join('');
  }
  if (React.isValidElement(children) && (children.props as any)?.children) {
    return extractTextFromChildren((children.props as any).children);
  }
  return '';
}

const ScrollableTable: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = React.useState(false);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const checkOverflow = () => {
      const isOverflowing = el.scrollWidth > el.clientWidth;
      const isScrolledToEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      setShowFade(isOverflowing && !isScrolledToEnd);
    };
    checkOverflow();
    el.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);
    return () => {
      el.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [children]);

  return (
    <div className="my-4 relative">
      <div
        ref={scrollRef}
        className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
      >
        <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          {children}
        </table>
      </div>
      {showFade && <div className="table-scroll-fade" />}
    </div>
  );
};

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '', onNoteClick, onImageClick, onPageLinkClick, onTimestampClick }) => {
  // Pre-process: translate `\(..\)` → `$..$` and `\[..\]` → `$$..$$`
  // so remark-math sees the dollar convention regardless of which
  // delimiter the narrator (or stored content) emits.
  const processedContent = translateBracketedMath(content);

  const customUrlTransform = (url: string) => {
    if (url.startsWith('s3://') || url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) return url;
    if (url.startsWith('#')) return url;
    return '';
  };

  return (
    <div className={`prose dark:prose-invert max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, [remarkMath, REMARK_MATH_OPTIONS]]}
        // Plugin order matters: rehype-katex must run BEFORE rehype-raw
        // so it converts `math` AST nodes to katex HAST BEFORE rehype-raw
        // touches any embedded raw HTML.
        rehypePlugins={[[rehypeKatex, REHYPE_KATEX_OPTIONS], rehypeRaw]}
        urlTransform={customUrlTransform}
        components={{
          h2: ({ node, children, ...props }) => (
            <h2 id={headingAnchorId(node)} className="text-xl font-bold mt-4 mb-2 flex items-center gap-2 scroll-mt-4" {...props}>
              {children}
            </h2>
          ),
          h3: ({ node, children, ...props }) => (
            <h3 className="text-lg font-semibold mt-3 mb-2" {...props}>
              {children}
            </h3>
          ),
          p: ({ node, ...props }) => <p className="mb-3 leading-relaxed" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-3 space-y-1" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-3 space-y-1" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900 dark:text-gray-100" {...props} />,
          a: ({ node, href, children, ...props }) => {
            const tsMatch = href?.match(/^#t=(\d+)/);
            if (tsMatch) {
              const sec = parseInt(tsMatch[1], 10);
              return (
                <a
                  href={href}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTimestampClick?.(sec); }}
                  className="not-prose inline-flex items-center gap-0.5 rounded-md bg-cyan-500/10 px-1.5 py-0.5 align-middle text-xs font-semibold text-cyan-700 no-underline transition hover:bg-cyan-500/20 dark:text-cyan-300"
                  title="Jump to this moment in the video"
                >
                  <span aria-hidden className="text-[10px]">▸</span>{children}
                </a>
              );
            }
            if (href?.startsWith('#note-')) {
              const targetNoteId = href.replace('#note-', '');
              return (
                <a
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onNoteClick && targetNoteId) {
                      onNoteClick(targetNoteId);
                    }
                  }}
                  className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium"
                  {...props}
                >
                  {children}
                </a>
              );
            }

            const pageLinkMatch = href?.match(/^\/book\/([^/]+)\/page\/(\d+)$/);
            if (pageLinkMatch) {
              const [, bookId, pageNumStr] = pageLinkMatch;
              const pageNum = parseInt(pageNumStr, 10);
              return (
                <ResolvedPageLink
                  href={href || ''}
                  bookId={bookId}
                  pageNum={pageNum}
                  onPageLinkClick={onPageLinkClick}
                >
                  {children}
                </ResolvedPageLink>
              );
            }

            return (
              <a
                className="text-blue-600 dark:text-blue-400 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                href={href}
                {...props}
              >
                {children}
              </a>
            );
          },
          img: ({ node, src, ...props }) => (
            <span
              className={onImageClick ? 'group relative inline-block cursor-zoom-in' : ''}
              onClick={(e) => {
                e.stopPropagation();
                if (onImageClick && src) {
                  onImageClick(src);
                }
              }}
            >
              <S3Image className="rounded-lg my-4 max-w-full hover:opacity-90 transition-opacity" alt="" src={src} {...props} />
              {/* Hover affordance — only for consumers that opt into onImageClick
                  (mirrors VidyaSvg's "Click to expand" pill). */}
              {onImageClick && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute right-2 top-6 rounded-md bg-slate-900/80 px-2 py-1 text-[11px] font-semibold text-white opacity-0 shadow transition-opacity group-hover:opacity-100"
                >
                  ⤢ Click to expand
                </span>
              )}
            </span>
          ),
          // Our custom fenced blocks (vidya-*) render their own components via the
          // `code` handler below. React-markdown otherwise wraps them in a <pre>,
          // whose `prose` styling forces monospace + no-wrap — so card labels can't
          // wrap and overflow horizontally. Strip the <pre> for those blocks; keep
          // it for genuine code.
          pre: ({ node, children, ...props }: any) => {
            const codeEl = node?.children?.find((c: any) => c.tagName === 'code');
            const cls = codeEl?.properties?.className;
            const langs = Array.isArray(cls) ? cls : typeof cls === 'string' ? [cls] : [];
            const CUSTOM = [
              'language-vidya-video-moments',
              'language-vidya-figure',
              'language-vidya-svg',
              'language-mermaid',
              'language-vidya-equation',
            ];
            if (langs.some((l: string) => CUSTOM.includes(l))) return <>{children}</>;
            return <pre {...props}>{children}</pre>;
          },
          code: ({ node, inline, className, children, ...props }: any) => {
            // Intercept ```vidya-video-moments``` fenced blocks → a clickable
            // moment-card list. The video locate path (find_video_moments) emits
            // JSON [{sec,label}]; each card seeks the player via onTimestampClick.
            if (!inline && className === 'language-vidya-video-moments') {
              const body = Array.isArray(children)
                ? children.join('')
                : String(children ?? '');
              return <VideoMoments body={body} onSeek={onTimestampClick} />;
            }
            // Intercept ```vidya-figure``` fenced code blocks and render via
            // <VidyaFigure /> instead of as plain <code>. Math-mode answers
            // emit these blocks for interactive geometry / function plots.
            // Renderer parses JSON, validates with Zod, and lazy-loads the
            // matching child (rough.js geometry or function-plot plot).
            if (!inline && className === 'language-vidya-figure') {
              const body = Array.isArray(children)
                ? children.join('')
                : String(children ?? '');
              return <VidyaFigure body={body} />;
            }
            // Intercept ```mermaid``` fenced code blocks and render via
            // <MermaidDiagram />. Used for flowcharts, sequence / state /
            // class diagrams, classification trees, timelines, etc. — any
            // structural diagram with well-known LLM-emission grammar.
            // Hand-drawn aesthetic matches VidyaFigure via mermaid's
            // `look: 'handDrawn'` option (also rough.js under the hood).
            if (!inline && className === 'language-mermaid') {
              const body = Array.isArray(children)
                ? children.join('')
                : String(children ?? '');
              return <MermaidDiagram body={body} />;
            }
            // Intercept ```vidya-svg``` fenced code blocks and render via
            // <VidyaSvg />. The backend's figure_curator agent (Gemini 3
            // Flash) emits clean SVG from a semantic vidya-figure-spec;
            // the streaming proxy sanitises it and wraps it in this
            // fence. VidyaSvg re-strokes the paths via svg2roughjs so
            // the hand-drawn aesthetic matches VidyaFigure + Mermaid.
            // Used for: FBDs, ray diagrams, circuits, annotated geometry,
            // inscribed-shape constructions — anything where the
            // narrator's vidya-figure JSON kept producing broken
            // coordinates.
            if (!inline && className === 'language-vidya-svg') {
              const body = Array.isArray(children)
                ? children.join('')
                : String(children ?? '');
              return <VidyaSvg body={body} />;
            }
            // Degraded-mode fallback for vidya-equation blocks. The backend
            // streaming proxy in agent_chat.py is supposed to buffer these,
            // run SymPy, and emit the result as $$...$$ markdown — so the
            // student should never see a raw block. If one ever leaks
            // through (proxy bug, mid-stream disconnect, etc.), at least
            // show a clear "verification unavailable" indicator with the
            // block contents inside, rather than blank-screening the math.
            if (!inline && className === 'language-vidya-equation') {
              const body = Array.isArray(children)
                ? children.join('')
                : String(children ?? '');
              return (
                <div className="my-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm">
                  <div className="font-medium text-amber-800 dark:text-amber-200 mb-2">
                    Verification unavailable — raw equation block:
                  </div>
                  <pre className="overflow-x-auto text-xs text-slate-700 dark:text-slate-300">
                    {body}
                  </pre>
                </div>
              );
            }
            return inline ? (
              <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm" {...props}>
                {children}
              </code>
            ) : (
              <code className="block bg-gray-100 dark:bg-gray-800 p-3 rounded-lg text-sm overflow-x-auto" {...props}>
                {children}
              </code>
            );
          },
          table: ({ node, children, ...props }: any) => (
            <ScrollableTable>{children}</ScrollableTable>
          ),
          thead: ({ node, ...props }: any) => (
            <thead className="bg-gray-50 dark:bg-gray-800/60" {...props} />
          ),
          th: ({ node, ...props }: any) => (
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider break-words" {...props} />
          ),
          tbody: ({ node, ...props }: any) => (
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" {...props} />
          ),
          td: ({ node, children, ...props }: any) => (
            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 break-words" {...props}>
              {processBrTags(children)}
            </td>
          ),
          tr: ({ node, children, ...props }: any) => {
            const text = extractTextFromChildren(children);
            const isDivergenceRow = text.includes('⚡');
            return (
              <tr
                className={
                  isDivergenceRow
                    ? 'divergence-row'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors'
                }
                {...props}
              >
                {children}
              </tr>
            );
          },
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};
